import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, skip } from 'rxjs';

import { BaseComponent } from 'src/app/base/base.component';
import { FtpServerTypeUtil } from 'src/app/domainObjects/dataConnector';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { OrganizationService } from 'src/app/views/organization/organization.service';
import {
  FtpServiceDialogComponent,
  FtpServiceDialogData,
  FtpServiceDialogResult
} from './ftp-service-dialog/ftp-service-dialog.component';
import { FtpConnectionResult, OrgFtpService, toFtpApiError } from './org-ftp-service.model';

/**
 * Organization Settings → FTP tab. Lists the organization's saved FTP services
 * read-only; adding and editing happen in FtpServiceDialogComponent, which saves
 * explicitly and returns here to refresh the list. Each service's connection is
 * tested in the background after the list is shown, and each row updates as soon
 * as its own test finishes.
 */
@Component({
  selector: 'app-ftp-setup',
  templateUrl: './ftp-setup.component.html',
  styleUrls: ['./ftp-setup.component.css'],
  standalone: false
})
export class FtpSetupComponent extends BaseComponent implements OnInit, OnDestroy {

  readonly FtpServerTypeUtil = FtpServerTypeUtil;
  readonly displayedColumns = ['ftpName', 'ftpServerType', 'ftpServerLocation', 'controlPort', 'userName', 'status', 'connection', 'actions'];

  /** Tests in flight from this page at once; the server also caps tests across all users. */
  static readonly MAX_PARALLEL_TESTS = 4;

  services: OrgFtpService[] = [];
  loading = false;
  loadError = '';
  /** Connection test results keyed by FTP service id — never by row index. */
  connection: Record<number, FtpConnectionResult> = {};
  /** Ids of services whose delete is in progress (their row's actions are disabled). */
  deleting = new Set<number>();

  // Bumped on every load (and on destroy) so test results that arrive after a
  // reload, an organization change or leaving the page are dropped.
  private loadGeneration = 0;

  private orgChangeSubscription?: Subscription;

  constructor(
    private organizationService: OrganizationService,
    private snackBar: MatSnackBar,
    matDialog: MatDialog,
    errorLogger: ErrorReportingService
  ) {
    super(errorLogger, matDialog);
  }

  ngOnInit(): void {
    this.loadServices();
    // Settings → Change Organization can switch organizations while this list is
    // showing; reload so it never shows the previous organization's services.
    this.orgChangeSubscription = this.organizationService.selectedOrganization$
      .pipe(skip(1))
      .subscribe(() => this.loadServices());
  }

  ngOnDestroy(): void {
    this.orgChangeSubscription?.unsubscribe();
    this.loadGeneration++;
  }

  async loadServices(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.loading = true;
    this.loadError = '';
    try {
      const services = await this.organizationService.getOrgFtpServices() ?? [];
      if (generation !== this.loadGeneration) {
        return;
      }
      this.services = services;
      // Show the list now; tests report into it as they finish.
      this.testConnections(services, generation);
    } catch (err) {
      if (generation === this.loadGeneration) {
        this.loadError = toFtpApiError(err).message;
      }
    } finally {
      if (generation === this.loadGeneration) {
        this.loading = false;
      }
    }
  }

  /**
   * Tests every active service in the background, at most MAX_PARALLEL_TESTS at
   * a time: a small pool of workers pulls from a shared queue, so each row is
   * updated the moment its own test completes, in whatever order they finish.
   */
  private testConnections(services: OrgFtpService[], generation: number): void {
    const connection: Record<number, FtpConnectionResult> = {};
    const queue: OrgFtpService[] = [];
    for (const service of services) {
      if (service.isActive === 1) {
        connection[service.id] = { state: 'testing', message: 'Testing…' };
        queue.push(service);
      } else {
        connection[service.id] = { state: 'not-tested', message: 'Inactive services are not tested.' };
      }
    }
    this.connection = connection;

    const worker = async (): Promise<void> => {
      while (queue.length > 0 && generation === this.loadGeneration) {
        const service = queue.shift()!;
        const result = await this.testConnection(service.id);
        if (generation === this.loadGeneration) {
          this.connection[service.id] = result;
        }
      }
    };
    const workers = Math.min(FtpSetupComponent.MAX_PARALLEL_TESTS, queue.length);
    for (let i = 0; i < workers; i++) {
      void worker();
    }
  }

  private async testConnection(id: number): Promise<FtpConnectionResult> {
    try {
      const result = await this.organizationService.testOrgFtpService(id);
      if (result?.success) {
        return { state: 'connected', message: 'Connected' };
      }
      if (result && result.tested === false) {
        return { state: 'not-tested', message: result.message };
      }
      return { state: 'failed', message: result?.message || 'FTP connection failed.' };
    } catch (err) {
      // The test request itself failed (session, permission, server error), so
      // the FTP server was never judged.
      return { state: 'not-tested', message: toFtpApiError(err).message };
    }
  }

  addService(): void {
    this.openDialog({});
  }

  editService(service: OrgFtpService): void {
    this.openDialog({ service });
  }

  /** Deletes a service after the admin confirms; the list is reloaded on success. */
  deleteService(service: OrgFtpService): void {
    if (this.deleting.has(service.id)) {
      return;
    }
    this.showConfirmDialog(
      'Delete FTP/SFTP Service',
      `Delete the FTP/SFTP service "${service.ftpName}"? This cannot be undone. ` +
      'Ingest and Data Writer configurations that use it will need another FTP service selected.',
      confirmed => { if (confirmed) { this.performDelete(service); } },
      'Delete'
    );
  }

  private async performDelete(service: OrgFtpService): Promise<void> {
    this.deleting.add(service.id);
    try {
      const result = await this.organizationService.deleteOrgFtpService(service.id);
      this.snackBar.open(result?.message || `FTP service "${service.ftpName}" deleted.`, 'Close', { duration: 3000 });
      await this.loadServices();
    } catch (err) {
      this.displayError(toFtpApiError(err).message, 'Delete FTP/SFTP Service');
    } finally {
      this.deleting.delete(service.id);
    }
  }

  trackById(_: number, service: OrgFtpService): number {
    return service.id;
  }

  private openDialog(data: FtpServiceDialogData): void {
    this.matDialog
      .open<FtpServiceDialogComponent, FtpServiceDialogData, FtpServiceDialogResult>(FtpServiceDialogComponent, {
        data,
        width: '640px',
        maxWidth: '95vw',
        disableClose: true,
        autoFocus: false
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.snackBar.open(result.message, 'Close', { duration: 3000 });
          this.loadServices();
        }
      });
  }
}
